import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { getConfig } from '../config/env';

const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000;

const getBaseUrl = (): string => {
  return getConfig().BASE_URL;
};

interface ApiResponse<T = any> {
  data: T;
  status: number;
  message?: string;
}

class ApiService {
  private axiosInstance: AxiosInstance | null = null;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
  }> = [];

  private getAxiosInstance(): AxiosInstance {
    if (!this.axiosInstance) {
      this.axiosInstance = axios.create({
        baseURL: getBaseUrl(),
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      this.setupInterceptors();
    }
    return this.axiosInstance;
  }

  private setupInterceptors(): void {
    if (!this.axiosInstance) return;

    this.axiosInstance.interceptors.request.use(
      (config) => {
        const authToken = sessionStorage.getItem('authToken');
        if (authToken) {
          config.headers.Authorization = `Bearer ${authToken}`;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.axiosInstance!.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then(() => {
              return this.getAxiosInstance()(originalRequest);
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          const refreshToken = localStorage.getItem('refreshToken');

          if (refreshToken) {
            try {
              const response = await axios.post(`${getBaseUrl()}/users/token_refresh/`, {
                refresh: refreshToken
              });

              const { access, user } = response.data;
              sessionStorage.setItem('authToken', access);
              sessionStorage.setItem('userId', user.id);

              this.processQueue(null);

              return this.getAxiosInstance()(originalRequest);
            } catch (refreshError) {
              this.processQueue(refreshError);
              this.handleAuthError();
              return Promise.reject(refreshError);
            } finally {
              this.isRefreshing = false;
            }
          } else {
            this.handleAuthError();
          }
        }

        this.handleApiError(error);
        return Promise.reject(error);
      }
    );
  }

  private processQueue(error: any): void {
    this.failedQueue.forEach(promise => {
      if (error) {
        promise.reject(error);
      } else {
        promise.resolve();
      }
    });
    this.failedQueue = [];
  }

  private handleAuthError(): void {
    sessionStorage.clear();
    localStorage.removeItem('refreshToken');
    window.location.href = '/login';
  }

  private handleApiError(error: AxiosError): void {
    const status = error.response?.status;

    if (process.env.NODE_ENV === 'development') {
      console.error(`❌ API Error: ${error.config?.url}`, error);
    }

    if (status === 401) return;
  }

  private async retryRequest<T>(
    requestFn: () => Promise<AxiosResponse<T>>,
    retries = MAX_RETRY_ATTEMPTS
  ): Promise<AxiosResponse<T>> {
    try {
      return await requestFn();
    } catch (error: any) {
      if (retries > 0 && (error.code === 'ECONNABORTED' || !error.response)) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return this.retryRequest(requestFn, retries - 1);
      }
      throw error;
    }
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.retryRequest(() =>
      this.getAxiosInstance().get<T>(url, config)
    );
    return {
      data: response.data,
      status: response.status,
    };
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.getAxiosInstance().post<T>(url, data, config);
    return {
      data: response.data,
      status: response.status,
    };
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.getAxiosInstance().put<T>(url, data, config);
    return {
      data: response.data,
      status: response.status,
    };
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.getAxiosInstance().patch<T>(url, data, config);
    return {
      data: response.data,
      status: response.status,
    };
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.getAxiosInstance().delete<T>(url, config);
    return {
      data: response.data,
      status: response.status,
    };
  }

  async uploadFile<T = any>(url: string, file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.getAxiosInstance().post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return {
      data: response.data,
      status: response.status,
    };
  }

  clearCache(key?: string): void {
  }
}

const apiService = new ApiService();
export default apiService;

export type { ApiResponse };
